import { useState, useCallback, useRef } from 'react';
import { Message, AgentPublicInfo } from '../types';

export type StreamingState = 'idle' | 'listening' | 'processing' | 'playing' | 'error';

/** Pipeline mode: Deepgram wants 16k. Realtime (S2S): the model speaks/hears 24k. */
export type VoiceMode = 'pipeline' | 'realtime';
const PIPELINE_SAMPLE_RATE = 16000;
const REALTIME_SAMPLE_RATE = 24000;

function getWsUrl(agentKey: string, resumeSessionId?: string): string {
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';
  const httpBase = apiBase.replace(/\/api$/, '');
  const wsBase = httpBase.replace(/^http/, 'ws');
  const resume = resumeSessionId ? `&sessionId=${encodeURIComponent(resumeSessionId)}` : '';
  return `${wsBase}/browser-stream?agentKey=${encodeURIComponent(agentKey)}${resume}`;
}

export function useStreamingSession() {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [agentInfo, setAgentInfo] = useState<AgentPublicInfo | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [state, setState] = useState<StreamingState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [voiceMode, setVoiceMode] = useState<VoiceMode>('pipeline');

  const wsRef = useRef<WebSocket | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const mutedRef = useRef(false);
  const stateRef = useRef<StreamingState>('idle');
  const voiceModeRef = useRef<VoiceMode>('pipeline');
  /** Realtime playback: sources currently scheduled, and the scheduling cursor. */
  const pcmSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const pcmNextStartRef = useRef(0);

  const setStateTracked = useCallback((s: StreamingState) => {
    stateRef.current = s;
    setState(s);
  }, []);

  const addMessage = useCallback((role: 'user' | 'assistant', text: string) => {
    const message: Message = {
      id: crypto.randomUUID(),
      role,
      text,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, message]);
  }, []);

  const stopPlayback = useCallback(() => {
    if (sourceRef.current) {
      try {
        sourceRef.current.stop();
      } catch {
        // already stopped
      }
      sourceRef.current = null;
    }
  }, []);

  const playMp3 = useCallback(async (data: ArrayBuffer) => {
    if (!audioCtxRef.current) return;
    const ctx = audioCtxRef.current;

    try {
      const audioBuffer = await ctx.decodeAudioData(data.slice(0));
      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);
      sourceRef.current = source;
      setStateTracked('playing');
      source.onended = () => {
        sourceRef.current = null;
        setStateTracked('listening');
      };
      source.start();
    } catch {
      setStateTracked('listening');
    }
  }, [setStateTracked]);

  /**
   * Realtime playback: the model streams raw PCM16LE @24k, so there is nothing to
   * decode — turn each chunk into an AudioBuffer and schedule it right after the
   * previous one, or the chunks would overlap and stutter.
   */
  const playPcm = useCallback((data: ArrayBuffer) => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    // Int16Array requires an even byte length; drop a trailing odd byte.
    const usable = data.byteLength - (data.byteLength % 2);
    if (!usable) return;
    const int16 = new Int16Array(data, 0, usable / 2);

    const buffer = ctx.createBuffer(1, int16.length, REALTIME_SAMPLE_RATE);
    const channel = buffer.getChannelData(0);
    for (let i = 0; i < int16.length; i++) channel[i] = int16[i] / 0x8000;

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    const startAt = Math.max(ctx.currentTime, pcmNextStartRef.current);
    source.start(startAt);
    pcmNextStartRef.current = startAt + buffer.duration;
    pcmSourcesRef.current.push(source);
    source.onended = () => {
      pcmSourcesRef.current = pcmSourcesRef.current.filter((s) => s !== source);
    };
  }, []);

  /** Barge-in: drop everything already scheduled, or the agent talks over the user. */
  const clearPcm = useCallback(() => {
    pcmSourcesRef.current.forEach((s) => {
      try {
        s.stop();
      } catch {
        // already stopped
      }
    });
    pcmSourcesRef.current = [];
    pcmNextStartRef.current = 0;
  }, []);

  const cleanupAudio = useCallback(() => {
    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
  }, []);

  const startMicrophone = useCallback(async (mode: VoiceMode) => {
    if (!wsRef.current) return;

    // The worklet emits PCM at the context's rate, so the context rate IS the
    // wire format: 16k for Deepgram (pipeline), 24k for the realtime model.
    const rate =
      mode === 'realtime' ? REALTIME_SAMPLE_RATE : PIPELINE_SAMPLE_RATE;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: rate,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      streamRef.current = stream;

      const audioCtx = new AudioContext({ sampleRate: rate });
      audioCtxRef.current = audioCtx;
      // Created after the WS round-trip, i.e. outside the connect click's user
      // gesture, so some browsers (Safari) start it suspended — which freezes
      // currentTime and stalls the realtime PCM scheduler. Resume defensively.
      if (audioCtx.state === 'suspended') await audioCtx.resume().catch(() => {});

      await audioCtx.audioWorklet.addModule('/pcm-processor.js');

      const source = audioCtx.createMediaStreamSource(stream);
      const workletNode = new AudioWorkletNode(audioCtx, 'pcm-processor');
      workletNodeRef.current = workletNode;

      workletNode.port.onmessage = (e: MessageEvent<ArrayBuffer>) => {
        const ws = wsRef.current;
        if (ws && ws.readyState === WebSocket.OPEN && !mutedRef.current) {
          ws.send(e.data);
        }
      };

      source.connect(workletNode);
      workletNode.connect(audioCtx.destination);

      setStateTracked('listening');
    } catch (e: any) {
      setError(e.message || 'Microphone access denied');
      setStateTracked('error');
    }
  }, [setStateTracked]);

  const connect = useCallback(async (
    apiKey: string,
    resumeSessionId?: string,
    initialMessages?: Message[],
  ) => {
    setError(null);
    if (initialMessages?.length) setMessages(initialMessages);

    return new Promise<{ sessionId: string; agent: AgentPublicInfo }>((resolve, reject) => {
      const url = getWsUrl(apiKey, resumeSessionId);
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.binaryType = 'arraybuffer';

      ws.onmessage = (event) => {
        if (event.data instanceof ArrayBuffer) {
          // Pipeline sends one MP3 blob per reply; realtime streams raw PCM.
          if (voiceModeRef.current === 'realtime') playPcm(event.data);
          else playMp3(event.data);
          return;
        }

        try {
          const msg = JSON.parse(event.data);

          switch (msg.type) {
            case 'session': {
              const mode: VoiceMode =
                msg.voiceMode === 'realtime' ? 'realtime' : 'pipeline';
              voiceModeRef.current = mode;
              setVoiceMode(mode);
              setSessionId(msg.sessionId);
              setAgentInfo(msg.agent);
              resolve({ sessionId: msg.sessionId, agent: msg.agent });
              // Auto-start microphone
              startMicrophone(mode);
              break;
            }

            case 'transcript':
              addMessage(msg.role, msg.text);
              if (msg.role === 'assistant') {
                setStateTracked('processing');
              }
              break;

            case 'speech_started':
              stopPlayback();
              break;

            // Realtime barge-in: the model was cut off, so drop the audio we
            // already queued ahead of the speakers.
            case 'clear':
              clearPcm();
              setStateTracked('listening');
              break;

            case 'playback_start':
              setStateTracked('playing');
              break;

            case 'playback_end':
              // Pipeline returns to 'listening' via the MP3 source's onended;
              // realtime has no single source, so close the turn here.
              if (voiceModeRef.current === 'realtime') {
                setStateTracked('listening');
              }
              break;

            case 'error':
              setError(msg.message);
              setStateTracked('error');
              break;
          }
        } catch {
          // ignore parse errors
        }
      };

      ws.onerror = () => {
        setError('WebSocket connection failed');
        setStateTracked('error');
        reject(new Error('WebSocket connection failed'));
      };

      ws.onclose = () => {
        wsRef.current = null;
        clearPcm();
        cleanupAudio();
      };
    });
  }, [addMessage, playMp3, playPcm, clearPcm, stopPlayback, cleanupAudio, startMicrophone, setStateTracked]);

  const toggleMute = useCallback(() => {
    const next = !mutedRef.current;
    mutedRef.current = next;
    setMuted(next);
  }, []);

  const sendTextMessage = useCallback((text: string) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !text.trim()) return;
    ws.send(JSON.stringify({ type: 'text', text: text.trim() }));
  }, []);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    cleanupAudio();
    stopPlayback();
    clearPcm();
    setSessionId(null);
    setAgentInfo(null);
    setMessages([]);
    setStateTracked('idle');
    setError(null);
    mutedRef.current = false;
    setMuted(false);
    voiceModeRef.current = 'pipeline';
    setVoiceMode('pipeline');
  }, [cleanupAudio, stopPlayback, clearPcm, setStateTracked]);

  const clearMessages = useCallback(() => setMessages([]), []);

  return {
    sessionId,
    agentInfo,
    messages,
    state,
    error,
    muted,
    // Realtime is voice-only — the wrapper has no way to inject a user text turn.
    voiceMode,
    connect,
    disconnect,
    toggleMute,
    sendTextMessage,
    clearMessages,
  };
}
